import { useApiTestingLanguage } from './useApiTestingLanguage';
/**
 * TestCard Component
 * 
 * Displays a single API test with its details, actions, and metadata.
 * Extracted from APITesting.js for better maintainability.
 */

import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Play,
  Clock,
  Calendar,
  Edit,
  Trash2,
  Folder
} from 'lucide-react';

// HTTP method badge colors
const getMethodColor = (method) => {
  const colors = {
    GET: 'bg-blue-100 text-blue-800',
    POST: 'bg-green-100 text-green-800',
    PUT: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
    PATCH: 'bg-purple-100 text-purple-800'
  };
  return colors[method] || 'bg-gray-100 text-gray-800';
};

export const TestCard = ({
  test,
  projects = [],
  onRun,
  onViewHistory,
  onSchedule,
  onEdit,
  onDelete
}) => {
  const { t, language } = useApiTestingLanguage();
  // Find the project for this test
  const project = test.project_id 
    ? projects.find(p => p.id === test.project_id) 
    : null;

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow" data-testid={`test-card-${test.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Badge className={getMethodColor(test.method)}>
            {test.method}
          </Badge>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{test.name}</h3>
            <p className="text-sm text-gray-600">{test.url}</p>
            {/* Phase 3: Display Project Name */}
            {project && (
              <div className="flex items-center space-x-1 mt-1 text-xs text-purple-600">
                <Folder className="h-3 w-3" />
                <span>{project.icon} {project.name}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex space-x-2">
          <Button
            onClick={() => onRun(test.id)}
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            data-testid={`btn-run-test-${test.id}`}
          >
            <Play className="h-4 w-4 mr-1" />
            {t("Run")}
          </Button>
          <Button
            onClick={() => onViewHistory(test)}
            size="sm"
            variant="outline"
            className="bg-blue-50 hover:bg-blue-100 text-blue-600"
            data-testid={`btn-history-test-${test.id}`}
          >
            <Clock className="h-4 w-4 mr-1" />
            {t("History")}
          </Button>
          <Button
            onClick={() => onSchedule(test)}
            size="sm"
            variant="outline"
            className="bg-purple-50 hover:bg-purple-100 text-purple-600"
            data-testid={`btn-schedule-test-${test.id}`}
          >
            <Calendar className="h-4 w-4 mr-1" />
            {t("Schedule")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label={t("Edit")}
            onClick={() => onEdit(test)}
            data-testid={`btn-edit-test-${test.id}`}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700"
            aria-label={t("Delete")}
            onClick={() => onDelete(test)}
            data-testid={`btn-delete-test-${test.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <p className="text-gray-600 mb-3">{test.description}</p>
      
      <div className="flex items-center space-x-4 text-sm text-gray-500">
        <span>{t("Created")} {new Date(test.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}</span>
        {test.environment_id && <span>{t("• Environment configured")}</span>}
        {test.credential_id && <span>{t("• Auth configured")}</span>}
      </div>
    </Card>
  );
};
